import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const fleetBrand = {
  teal: "#4A9B8E",
  tealLight: "#5AADA0",
  aqua: "#82C5BB",
};
import dashboardImg from "@/assets/fleet/dashboard.png";
import dashboardFrotaImg from "@/assets/fleet/dashboard-frota.png";
import veiculosImg from "@/assets/fleet/veiculos.png";
import motoristasImg from "@/assets/fleet/motoristas.png";
import alocacaoImg from "@/assets/fleet/alocacao.png";
import manutencaoImg from "@/assets/fleet/manutencao.png";
import fornecedoresImg from "@/assets/fleet/fornecedores.png";
import relatorioManutencaoImg from "@/assets/fleet/relatorio-manutencao.png";
import relatorioOperacionalImg from "@/assets/fleet/relatorio-operacional.png";
import relatorioVeiculosImg from "@/assets/fleet/relatorio-veiculos.png";

interface DemoScreen {
  id: number;
  title: string;
  description: string;
  features: string[];
  image: string;
}

const demoScreens: DemoScreen[] = [
  {
    id: 1,
    title: "Painel Administrativo",
    description: "Personalize seu dashboard administrativo com widgets dos sistemas liberados para sua empresa.",
    features: [
      "Widgets personalizáveis por sistema",
      "Calendário de manutenção integrado",
      "Ações rápidas para cadastros",
      "Notificações e alertas centralizados"
    ],
    image: dashboardImg
  },
  {
    id: 2,
    title: "Dashboard da Frota",
    description: "Tenha uma visão completa da sua frota em tempo real com KPIs essenciais e métricas de performance.",
    features: [
      "Veículos ativos e em manutenção",
      "Resumo de custos mensais",
      "Performance da frota em tempo real",
      "Ações rápidas e alertas recentes"
    ],
    image: dashboardFrotaImg
  },
  {
    id: 3,
    title: "Gestão de Veículos",
    description: "Controle completo da sua frota com informações detalhadas de cada veículo.",
    features: [
      "Cadastro completo de veículos",
      "Controle de documentação",
      "Acompanhamento de quilometragem",
      "Status e centro de custo"
    ],
    image: veiculosImg
  },
  {
    id: 4,
    title: "Gerenciamento de Motoristas",
    description: "Gerencie sua equipe de motoristas com controle de CNH e documentação.",
    features: [
      "Cadastro de motoristas",
      "Controle de CNH e vencimentos",
      "Histórico de infrações",
      "Gestão de contatos"
    ],
    image: motoristasImg
  },
  {
    id: 5,
    title: "Manutenção Preventiva",
    description: "Gerencie planos de manutenção e ordens de serviço da frota.",
    features: [
      "Planos de manutenção automatizados",
      "Ordens de serviço completas",
      "Controle de custos por fornecedor",
      "Alertas de manutenção pendente"
    ],
    image: manutencaoImg
  },
  {
    id: 6,
    title: "Alocação de Veículos",
    description: "Controle de alocação de veículos e motoristas para otimizar sua operação.",
    features: [
      "Gerenciamento de alocações",
      "Controle de centros de custo",
      "Status de alocações ativas",
      "Histórico de alocações"
    ],
    image: alocacaoImg
  },
  {
    id: 7,
    title: "Gestão de Fornecedores",
    description: "Cadastro e gestão de fornecedores de serviços da frota.",
    features: [
      "Cadastro de oficinas e postos",
      "Controle de documentação",
      "Avaliação de fornecedores",
      "Gestão de contatos"
    ],
    image: fornecedoresImg
  },
  {
    id: 8,
    title: "Relatórios de Manutenção",
    description: "Análise completa de ordens de serviço, custos e manutenções pendentes.",
    features: [
      "Total de OS e custos",
      "Análise por fornecedor",
      "Evolução mensal de custos",
      "Manutenções realizadas e pendentes"
    ],
    image: relatorioManutencaoImg
  },
  {
    id: 9,
    title: "Relatórios Operacionais",
    description: "Análise de viagens, combustível, alertas e performance operacional.",
    features: [
      "Viagens e distância percorrida",
      "Eficiência de combustível",
      "Velocidade média e tempo ocioso",
      "Alertas por categoria"
    ],
    image: relatorioOperacionalImg
  },
  {
    id: 10,
    title: "Relatórios de Veículos",
    description: "Análise detalhada de performance e utilização dos veículos da frota.",
    features: [
      "Performance individual por veículo",
      "Custos e manutenções",
      "Análise de utilização",
      "Métricas de eficiência"
    ],
    image: relatorioVeiculosImg
  }
];

export function FleetSystemDemo() {
  const [currentScreen, setCurrentScreen] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentScreen((prev) => (prev + 1) % demoScreens.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToScreen = (index: number) => {
    setCurrentScreen(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000); // Resume auto-play after 10s
  };

  const goToPrevious = () => {
    setCurrentScreen((prev) => (prev - 1 + demoScreens.length) % demoScreens.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToNext = () => {
    setCurrentScreen((prev) => (prev + 1) % demoScreens.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const openModal = (index: number) => {
    setModalImageIndex(index);
    setIsModalOpen(true);
    setIsAutoPlaying(false);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setIsAutoPlaying(true), 1000);
  };

  const goToNextImage = () => {
    setModalImageIndex((prev) => (prev + 1) % demoScreens.length);
  };

  const goToPreviousImage = () => {
    setModalImageIndex((prev) => (prev - 1 + demoScreens.length) % demoScreens.length);
  };

  useEffect(() => {
    if (!isModalOpen) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowRight') goToNextImage();
      if (e.key === 'ArrowLeft') goToPreviousImage();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen]);

  const currentDemo = demoScreens[currentScreen];

  return (
    <section className="py-20 bg-gradient-to-br from-background via-background/80 to-primary/5">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Explore o FleetONE na Prática
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Veja como nosso sistema pode transformar a gestão da sua frota
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Screen showcase */}
          <div className="relative">
            <div
              className="group relative overflow-hidden rounded-xl shadow-2xl bg-white"
              onMouseEnter={() => setIsAutoPlaying(false)}
              onMouseLeave={() => setIsAutoPlaying(true)}
            >
              {/* Navigation arrows */}
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={goToPrevious}
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={goToNext}
              >
                <ChevronRight className="w-5 h-5" />
              </Button>

              {/* Screen image */}
              <div className="aspect-video relative overflow-hidden cursor-pointer group/image" onClick={() => openModal(currentScreen)}>
                <img
                  src={currentDemo.image}
                  alt={currentDemo.title}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
                
                {/* Ícone de zoom que aparece ao passar o mouse */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/image:opacity-100 transition-opacity flex items-center justify-center">
                  <ZoomIn className="w-12 h-12 text-white" />
                </div>
                
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-xl font-semibold">{currentDemo.title}</h3>
                </div>
              </div>
            </div>

            {/* Dots indicator */}
            <div className="flex justify-center mt-6 gap-2">
              {demoScreens.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToScreen(index)}
                  style={
                    index === currentScreen
                      ? {
                          backgroundColor: fleetBrand.teal,
                          transform: "scale(1.25)",
                        }
                      : {
                          backgroundColor: "rgba(0,0,0,0.3)",
                        }
                  }
                  className="w-3 h-3 rounded-full transition-all duration-300 hover:bg-muted-foreground/50"
                />
              ))}
            </div>
          </div>

          {/* Right side - Description and features */}
          <div className="space-y-6">
            <div className="animate-fade-in" key={currentScreen}>
              <h3 className="text-2xl font-bold text-foreground mb-4">
                {currentDemo.title}
              </h3>
              <p className="text-lg text-muted-foreground mb-6">
                {currentDemo.description}
              </p>
              
              <div className="space-y-3">
                <h4 className="font-semibold text-foreground">Principais funcionalidades:</h4>
                <ul className="space-y-2">
                  {currentDemo.features.map((feature, index) => (
                    <li 
                      key={index}
                      className="flex items-center gap-3 text-muted-foreground"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <div 
                        className="w-2 h-2 rounded-full flex-shrink-0" 
                        style={{ backgroundColor: fleetBrand.teal }}
                      />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="pt-6">
              <button
                className="group relative overflow-hidden rounded-2xl px-6 py-3 font-medium text-white shadow-md transition hover:shadow-lg text-lg"
                style={{
                  background: `linear-gradient(90deg, ${fleetBrand.teal}, ${fleetBrand.aqua})`,
                  boxShadow: `0 0 30px ${fleetBrand.teal}44, 0 0 50px ${fleetBrand.aqua}33`,
                }}
              >
                <span className="relative z-10 flex items-center gap-2">
                  Experimente Gratuitamente
                  <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </span>
                <span
                  className="absolute inset-0 -z-0 opacity-0 transition-opacity group-hover:opacity-20"
                  style={{ background: `radial-gradient(80% 80% at 50% 50%, white, transparent)` }}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Visualização em Tela Cheia */}
      <Dialog open={isModalOpen} onOpenChange={closeModal}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-none">
          <div className="relative w-full h-[95vh] flex items-center justify-center">
            {/* Botão Fechar */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 z-50 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white"
              onClick={closeModal}
            >
              <X className="w-6 h-6" />
            </Button>

            {/* Botão Anterior */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-4 top-1/2 -translate-y-1/2 z-50 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white"
              onClick={goToPreviousImage}
            >
              <ChevronLeft className="w-8 h-8" />
            </Button>

            {/* Imagem em Tela Cheia */}
            <div className="w-full h-full flex items-center justify-center p-8">
              <img
                src={demoScreens[modalImageIndex].image}
                alt={demoScreens[modalImageIndex].title}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              />
            </div>

            {/* Botão Próxima */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-1/2 -translate-y-1/2 z-50 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white"
              onClick={goToNextImage}
            >
              <ChevronRight className="w-8 h-8" />
            </Button>

            {/* Informações da Imagem */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center text-white z-50">
              <h3 className="text-2xl font-bold mb-2">
                {demoScreens[modalImageIndex].title}
              </h3>
              <p className="text-sm text-white/80">
                {modalImageIndex + 1} de {demoScreens.length}
              </p>
            </div>

            {/* Dots indicadores */}
            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2 z-50">
              {demoScreens.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setModalImageIndex(index)}
                  className={cn(
                    "w-2 h-2 rounded-full transition-all duration-300",
                    index === modalImageIndex
                      ? "bg-white scale-125"
                      : "bg-white/30 hover:bg-white/50"
                  )}
                />
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}